import type {Message, TimelineEntry} from './hooks'
import {buildWorkingSegments} from './buildWorkingSegments'
import {partitionTimelineIntoOrderedSections} from './timelinePartition'

export type WorkingToolStepCounts = {
  total: number
  failed: number
}

function isFailedResult(entry: Extract<TimelineEntry, {kind: 'tool_result'}> | undefined): boolean {
  return Boolean(entry && entry.ok === false)
}

function countsFromWorkingEntries(entries: TimelineEntry[]): WorkingToolStepCounts {
  const segs = buildWorkingSegments(entries)
  let total = 0
  let failed = 0
  for (const seg of segs) {
    if (seg.type !== 'tool') continue
    total += 1
    if (isFailedResult(seg.result)) failed += 1
  }
  return {total, failed}
}

function lookupResult(
  entries: TimelineEntry[],
  toolCallId: string,
): Extract<TimelineEntry, {kind: 'tool_result'}> | undefined {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const e = entries[i]
    if (e?.kind === 'tool_result' && e.tool_call_id === toolCallId) return e
  }
  return undefined
}

/**
 * Count tool-call rows that render inside the Working strip (timeline working
 * buckets, or the live queue while a turn is still streaming).
 */
export function countWorkingToolSteps(
  msg?: Pick<Message, 'timeline' | 'liveWorkingQueue' | 'isStreamingTurn'>,
): WorkingToolStepCounts {
  if (!msg) return {total: 0, failed: 0}

  if (msg.isStreamingTurn) {
    const entries = msg.timeline?.entries ?? []
    const queue = msg.liveWorkingQueue ?? []
    let total = 0
    let failed = 0
    for (const item of queue) {
      if (item.kind !== 'tool') continue
      total += 1
      if (isFailedResult(lookupResult(entries, item.toolCallId))) failed += 1
    }
    return {total, failed}
  }

  const sections = partitionTimelineIntoOrderedSections(msg.timeline?.entries ?? [])
  let total = 0
  let failed = 0
  for (const sec of sections) {
    if (sec.kind !== 'working') continue
    const next = countsFromWorkingEntries(sec.entries)
    total += next.total
    failed += next.failed
  }
  return {total, failed}
}

/** Parent Working title, e.g. `Working (14 steps)`. */
export function formatWorkingTitle(total: number): string {
  if (total <= 0) return 'Working'
  return total === 1 ? 'Working (1 step)' : `Working (${total} steps)`
}

/** Trailing fail meta for the Working header. */
export function formatWorkingFailMeta(failed: number): string | undefined {
  if (failed <= 0) return undefined
  return failed === 1 ? '1 failed' : `${failed} failed`
}

export function formatIncompleteErrorText(status: {type: string; error?: unknown}): string | null {
  if (status.type !== 'incomplete' || status.error == null) return null
  return typeof status.error === 'string' ? status.error : JSON.stringify(status.error)
}

const ERROR_META_MAX = 48

function firstLine(text: string): string {
  const line = text.split(/\r?\n/, 1)[0]?.trim() ?? ''
  return line
}

/** Trailing status for a collapsed tool row (line count, done, or error snippet). */
export function formatToolResultMeta(opts: {
  pending?: boolean
  failed?: boolean
  errorText?: string | null
  resultText?: string
}): string | undefined {
  if (opts.pending) return undefined
  if (opts.failed) {
    const err = firstLine(opts.errorText ?? '')
    if (!err) return 'failed'
    return err.length > ERROR_META_MAX ? `${err.slice(0, ERROR_META_MAX - 1)}…` : err
  }
  const text = (opts.resultText ?? '').replace(/\s+$/, '')
  if (!text) return 'done'
  const lines = text.split(/\r?\n/).length
  return lines === 1 ? '1 line of output' : `${lines} lines of output`
}
